import { Email } from "@/Common/Api/Contacts/Dto/Email";
import { FlatLookupView } from "@/Common/Api/MedfarDictionaries/Dto/Responses/FlatLookupView";
import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { Patient } from "@/Common/Models/Patient/Patient";

interface ISelectEmailComponentProps {
    patientInfo: Patient;
    onCloseDelegate?: () => void;
    onSelectionCompletedDelegate?: (address: string, selectedContactTypeLookupId: string) => void;
    onShouldAddEmailToPatientContactChangeDelegate?: (addEmailToPatientContact: boolean) => void;
    onEnterKeyDelegate?: () => void;
}

class SelectEmailComponentState {
    public useCustomAddress: boolean;
    public customAddress: string;
    public contactTypes: FlatLookupView[];
    public selectedContactType: FlatLookupView;
    public addresses: EmailAddressEntry[];
    public selectedAddress: string;
    public shouldAddEmailToPatientContact: boolean;

    public static getDefault(patientInfo: Patient): SelectEmailComponentState {
        let s = new SelectEmailComponentState();
        s.selectedAddress = "";
        s.customAddress = "";

        s.addresses = patientInfo.Emails
            .map((e: Email) => new EmailAddressEntry(e));

        if (s.addresses.length > 0) {
            s.addresses[0].selected = true;
            s.useCustomAddress = false;
            s.selectedAddress = s.addresses[0].address;
        }
        else {
            s.useCustomAddress = true;
        }

        s.contactTypes = cacheStore.FindLookupsByTypeCode("CONTACTPOINTTYPE");
        s.selectedContactType = s.contactTypes[0];
        s.shouldAddEmailToPatientContact = false;

        return s;
    }
}

class EmailAddressEntry {
    public id: string;
    public address: string;
    public type: string;
    public typeEn: string;
    public selected: boolean;
    public typeLookupId: string;

    constructor(e: Email) {
        const ls = cacheStore.GetLookupById(e.ContactPointTypeLookupId);
        let typeName = "";

        if (ls) {
            typeName = ls.GetValue();
            this.typeEn = ls.en;
        }
        else {
            this.typeEn = "";
        }

        this.id = e.ContactPoint_id;
        this.address = e.Address;
        this.type = typeName;
        this.selected = false;
        this.typeLookupId = e.ContactPointTypeLookupId;
    }
}

class SelectEmailComponent extends BaseReactComponent<ISelectEmailComponentProps, SelectEmailComponentState> {
    private emailPattern: RegExp = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    private otherEmailFieldRef: HTMLInputElement;

    constructor(props: ISelectEmailComponentProps) {
        super(props);
        this.state = SelectEmailComponentState.getDefault(this.props.patientInfo);
    }

    public componentDidMount(): void {
        this.onEmailChanged();
    }

    public render(): JSX.Element {
        const lang = sessionStore.userLanguageCode;
        let hasEmails = this.props.patientInfo.Emails.length > 0 && this.props.patientInfo.Emails != null;

        return (
            <div className="content">

                <div>
                    {this.state.addresses.map((item: EmailAddressEntry, idx: number) => {
                        const fieldId = `email-option-${idx}`;
                        return (
                            <div key={idx + "___n"} className="flex-container space m">
                                <div className="flx-col center a">
                                    <input id={fieldId} name="email-option" checked={item.selected} onChange={() => this.setSelected(item)} className="pointer" type="radio" />
                                </div>
                                <div className="flx-col b">
                                    <div className="flex-container n">
                                        <div className="flx-col g right">
                                            <label htmlFor={fieldId}>{item.type}</label>
                                        </div>
                                        <div className="flx-col pl14 h">
                                            <label htmlFor={fieldId}>{item.address}</label>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}

                    {hasEmails && (
                        <div style={{ padding: "10px 0" }}>
                            <strong>{cacheStore.GetString("OtherLabel")}</strong>
                        </div>
                    )}

                </div>

                <div>
                    <div className="flex-container m">
                        {hasEmails && (
                            <div className="flx-col center a">
                                <input type="radio" onChange={() => this.setUseCustomEmail()} checked={this.state.useCustomAddress} className="pointer" name="email-option" id="email-option-other" />
                            </div>
                        )}

                        <div className={hasEmails ? "flx-col b" : "flx-col"}>
                            <div className="flex-container pp n">
                                <div className="flx-col g right">
                                    <label>{cacheStore.GetString("TypeLabel")}</label>
                                </div>
                                <div className="flx-col h">
                                    <select
                                        onFocus={() => this.setUseCustomEmail()}
                                        value={this.state.selectedContactType.LookupID}
                                        onChange={(e: any) => {
                                            let val = e.target.value;
                                            this.onContactTypeChange(val);
                                        }}
                                        className="myle-control form-control"
                                    >
                                        {this.state.contactTypes.map((l: FlatLookupView) => {
                                            var label = lang === "en" ? l.en : l.fr;
                                            return <option key={l.LookupID} value={l.LookupID}>{label}</option>;
                                        })}
                                    </select>
                                </div>
                            </div>

                            <div className="flex-container pp n">
                                <div className="flx-col g right">
                                    <label>{cacheStore.GetString("EmailLabel")}</label>
                                </div>
                                <div className="flx-col h">
                                    <input
                                        type="text"
                                        ref={element => this.otherEmailFieldRef = element}
                                        onFocus={() => this.setUseCustomEmail()}
                                        className="form-control"
                                        value={this.state.customAddress}
                                        onChange={(e: any) => {
                                            let val = e.target.value;
                                            this.onCustomEmailChange(val);
                                        }}
                                        placeholder={cacheStore.GetString("TypeEmailLabel")}
                                        onKeyUp={e => e.key === "Enter" && this.emailPattern.test(this.state.customAddress) === true && this.props.onEnterKeyDelegate && this.props.onEnterKeyDelegate()}
                                    />
                                </div>
                            </div>

                            {this.props.onShouldAddEmailToPatientContactChangeDelegate && (
                                <div className="flex-container pp n">
                                    <div className="flx-col g right" />
                                    <div className="flx-col h">

                                        <input
                                            id="TelemedInvitationSaveNewEmail"
                                            type="checkbox"
                                            style={{ margin: "15px 5px 0 2px" }}
                                            checked={this.state.shouldAddEmailToPatientContact}
                                            onClick={() => this.onAddEmailToPatientContactCheckboxClick()}
                                        />

                                        <label htmlFor="TelemedInvitationSaveNewEmail">
                                            {cacheStore.GetString("AddEmailToPatientContactInformationLabel")}
                                        </label>

                                    </div>
                                </div>
                            )}

                        </div>
                    </div>
                </div>

            </div>
        );
    }

    private setSelected(item: EmailAddressEntry): void {
        this.mutateState(s => {
            let selectedAddress: string = "";
            s.addresses = this.state.addresses.map((a: EmailAddressEntry) => {
                a.selected = item.id === a.id;
                if (a.selected)
                    selectedAddress = a.address;
                return a;
            });

            s.selectedAddress = selectedAddress;
            s.useCustomAddress = false;
        }, () => this.onEmailChanged());
    }

    private setUseCustomEmail(): void {
        this.focusOtherEmailField();

        this.mutateState(s => {
            s.addresses = this.state.addresses.map(a => {
                a.selected = false;
                return a;
            });
            s.useCustomAddress = true;
        }, () => this.onEmailChanged());
    }

    private onCustomEmailChange(val: string): void {
        this.mutateState(
            s => {
                s.customAddress = val;
            },
            () => this.onEmailChanged()
        );
    }

    private focusOtherEmailField(): void {
        this.otherEmailFieldRef.focus();
    }

    private onAddEmailToPatientContactCheckboxClick(): void {
        this.setUseCustomEmail();

        this.mutateState(
            s => { s.shouldAddEmailToPatientContact = !s.shouldAddEmailToPatientContact; },
            () => this.delegateOnAddEmailToPatientContactCheckboxChange(this.shouldAddEmailToPatientContact())
        );
    }

    private shouldAddEmailToPatientContact(): boolean {
        return this.state.useCustomAddress && this.state.shouldAddEmailToPatientContact;
    }

    private delegateOnAddEmailToPatientContactCheckboxChange(val: boolean): void {
        if (this.props.onShouldAddEmailToPatientContactChangeDelegate != null) {
            this.props.onShouldAddEmailToPatientContactChangeDelegate(val);
        }
    }

    private onContactTypeChange(value: string): void {
        this.mutateState(
            s => s.selectedContactType = this.state.contactTypes.filter(t => t.LookupID === value)[0],
            () => this.onEmailChanged()
        );
    }

    private onEmailChanged(): void {
        if (this.props.onSelectionCompletedDelegate != null) {
            if (this.state.useCustomAddress) {
                if (this.emailPattern.test(this.state.customAddress) === false)
                    this.props.onSelectionCompletedDelegate("", "");
                else
                    this.props.onSelectionCompletedDelegate(this.state.customAddress, this.state.selectedContactType.LookupID);
            }
            else
                this.props.onSelectionCompletedDelegate(this.state.selectedAddress, this.state.selectedContactType.LookupID);
        }

        this.delegateOnAddEmailToPatientContactCheckboxChange(this.shouldAddEmailToPatientContact());
    }
}

export { SelectEmailComponent };
